import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { SearchSelect } from './FieldComponents';
import '../styles/Weather.css';
import { Loader, Loader2, ChevronDown, Printer, X, Download, Filter } from 'lucide-react';

function Pages({ data, pageSize, currentPage, totalPages, handlePageChange, set }) {
    return (
        <div className="pages" style={set ? {position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)' } : {}}>
            <button className='weather-btns action' onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0}>
                Previous
            </button>
            <span>
                Page {currentPage + (totalPages > 0 ? 1 : 0)} of {totalPages}
            </span>
            <button className='weather-btns action' onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages - 1}>
                Next
            </button>
        </div>
    );
}

function SchoolEventsTable({ schoolId, schoolName, onClose }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const handlePrint = () => {
        const printContent = document.querySelector('.modal-content-wrapper');
        const schoolNameForPrint = schoolName;
        
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Weather Events - ${schoolNameForPrint}</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        h1 { color: #333; margin-bottom: 20px; }
                        table { border-collapse: collapse; width: 100%; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #f2f2f2; font-weight: bold; }
                        tr:nth-child(even) { background-color: #f9f9f9; }
                        .print-date { font-size: 12px; color: #666; margin-top: 20px; }
                    </style>
                </head>
                <body>
                    <h1>Weather Events for ${schoolNameForPrint}</h1>
                    <p>Found ${events.length} weather event${events.length !== 1 ? 's' : ''} in the last 2 years</p>
                    ${printContent.innerHTML}
                    <div class="print-date">Generated on: ${new Date().toLocaleDateString()}</div>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const handleExportModal = () => {
        // Import XLSX dynamically
        import('xlsx').then((XLSX) => {
            // Prepare data for Excel
            const excelData = events.map(event => ({
                'Date': formatDate(event.event_date),
                'Event Type': toTitleCase(event.event_type),
                'Magnitude': event.event_magnitude || 'N/A',
                'Property Damage': event.event_property_damage ? `$${parseInt(event.event_property_damage).toLocaleString()}` : 'N/A',
                'Begin City': event.begin_city || 'N/A',
                'End City': event.end_city || 'N/A',
                'Source': event.source || 'N/A'
            }));

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Add column widths
            const colWidths = [
                { wch: 12 }, // Date
                { wch: 15 }, // Event Type
                { wch: 12 }, // Magnitude
                { wch: 18 }, // Property Damage
                { wch: 20 }, // Begin City
                { wch: 20 }, // End City
                { wch: 15 }  // Source
            ];
            ws['!cols'] = colWidths;

            // Add the worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Weather Events');

            // Generate filename
            const dateStr = new Date().toISOString().split('T')[0];
            const safeName = schoolName.replace(/[^a-zA-Z0-9]/g, '-');
            const filename = `weather-events-${safeName}-${dateStr}.xlsx`;

            // Save file
            XLSX.writeFile(wb, filename);
        }).catch(error => {
            console.error('Error loading XLSX library:', error);
            alert('Error exporting to Excel. Please try again.');
        });
    };

    useEffect(() => {
        document.title = 'Weather Events | CaseDB';
    }, []);

    useEffect(() => {
        const fetchSchoolEvents = async () => {
            try {
                setLoading(true);
                const response = await fetch(`https://api.casedb.co/school_events.php?school_id=${schoolId}`);
                const data = await response.json();
                
                if (data.error) {
                    setError(data.error);
                } else {
                    setEvents(data);
                }
            } catch (err) {
                setError('Failed to fetch school events');
                console.error('Error fetching school events:', err);
            } finally {
                setLoading(false);
            }
        };

        if (schoolId) {
            fetchSchoolEvents();
        }
    }, [schoolId]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const toTitleCase = (str) => {
        if (!str) return '';
        return str.toLowerCase()
            .replace(/\b\w/g, l => l.toUpperCase())
            .replace(/\bIsd\b/g, 'ISD');
    };

    if (loading) {
        return (
            <Modal 
                title={`Loading events for ${schoolName}...`}
                onClose={onClose}
                wider={true}
            >
                <div className="modal-content-wrapper">
                    <div className="loading-spinner">Loading...</div>
                </div>
            </Modal>
        );
    }

    if (error) {
        return (
            <Modal 
                title="Error"
                onClose={onClose}
                wider={true}
            >
                <div className="modal-content-wrapper">
                    <div className="error-message">{error}</div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal 
            title={`Weather Events for ${schoolName}`}
            instructions={`Found ${events.length} weather event${events.length !== 1 ? 's' : ''} in the last 2 years`}
            onClose={onClose}
            wider={true}
        >
            <div className='modal-content-wrapper'>
            <div className="modal-header-actions">
                <button 
                    className="print-button"
                    onClick={handlePrint}
                    title="Print Events"
                >
                    <Printer size={16} />
                    Print
                </button>
                <button 
                    className="export-button"
                    onClick={handleExportModal}
                    title="Export to Excel"
                >
                    <Download size={16} />
                    Excel
                </button>
            </div>
                {events.length === 0 ? (
                    <p>No weather events found for this school in the last 2 years.</p>
                ) : (
                    <table className="weather-table events-wrapper">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Event Type</th>
                                <th className="hide-mobile">Magnitude</th>
                                <th>Property Damage</th>
                                <th className="hide-mobile">Begin City</th>
                                <th className="hide-mobile">End City</th>
                                <th className="hide-mobile">Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((event, index) => (
                                <tr key={index} className='small-text'>
                                    <td>{formatDate(event.event_date)}</td>
                                    <td>{toTitleCase(event.event_type)}</td>
                                    <td className="hide-mobile">{event.event_magnitude || 'N/A'}</td>
                                    <td>{event.event_property_damage ? `$${parseInt(event.event_property_damage).toLocaleString()}` : 'N/A'}</td>
                                    <td className="hide-mobile">{event.begin_city || 'N/A'}</td>
                                    <td className="hide-mobile">{event.end_city || 'N/A'}</td>
                                    <td className="hide-mobile">{event.source || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </Modal>
    );
}

export default function Weather({ theme }) {
    const [schools, setSchools] = useState([]);
    const [filteredSchools, setFilteredSchools] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSchool, setSelectedSchool] = useState(null);
    
    // Filter states
    const [selectedSchoolNames, setSelectedSchoolNames] = useState([]);
    const [selectedDistrictNames, setSelectedDistrictNames] = useState([]);
    const [showFilters, setShowFilters] = useState(true);
    
    // Debug selectedSchool changes
    useEffect(() => {
        console.log('selectedSchool changed:', selectedSchool);
    }, [selectedSchool]);
    
    // Pagination states
    const [pageSize, setPageSize] = useState(50);
    const [currentPage, setCurrentPage] = useState(0);

    // Sorting states
    const [sortColumn, setSortColumn] = useState('district_name');
    const [sortDirection, setSortDirection] = useState('desc');

    // Fetch schools data
    const fetchSchools = async (search = '') => {
        try {
            setLoading(true);
            const searchParam = search ? `?search=${encodeURIComponent(search)}` : '';
            const response = await fetch(`https://api.casedb.co/schools_list.php${searchParam}`);
            const data = await response.json();
            
            if (data.error) {
                console.error('Error fetching schools:', data.error);
            } else {
                setSchools(data);
                setFilteredSchools(data);
            }
        } catch (error) {
            console.error('Error fetching schools:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSchools();
    }, []);

    // Apply filters whenever filter states change
    useEffect(() => {
        let filtered = schools;
        
        // Apply search term filter
        if (searchTerm !== '') {
            filtered = filtered.filter(school => 
                school.school_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                school.district_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                school.city.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Apply school name filter
        if (selectedSchoolNames.length > 0) {
            filtered = filtered.filter(school => 
                selectedSchoolNames.includes(school.school_name)
            );
        }
        
        // Apply district name filter
        if (selectedDistrictNames.length > 0) {
            filtered = filtered.filter(school => 
                selectedDistrictNames.includes(school.district_name)
            );
        }
        
        setFilteredSchools(filtered);
        setCurrentPage(0); // Reset to first page when filters change
    }, [schools, searchTerm, selectedSchoolNames, selectedDistrictNames]);

    // Handle search
    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        // The filtering will be handled by the useEffect above
    };

    // Handle server-side search (on Enter or after delay)
    const handleServerSearch = () => {
        fetchSchools(searchTerm);
        setCurrentPage(0);
    };

    // Handle sorting
    const handleSort = (column) => {
        const direction = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortColumn(column);
        setSortDirection(direction);

        const sorted = [...filteredSchools].sort((a, b) => {
            let aVal = a[column];
            let bVal = b[column];

            // Handle numeric columns
            if (column === 'event_count' || column === 'enrollment' || column === 'district_size') {
                aVal = parseInt(aVal) || 0;
                bVal = parseInt(bVal) || 0;
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }

            if (direction === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        setFilteredSchools(sorted);
    };

    // Handle row click
    const handleRowClick = (school) => {
        setSelectedSchool(school);
    };

    // Pagination
    const totalPages = Math.ceil(filteredSchools.length / pageSize);
    const currentPageData = filteredSchools.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

        // Create search options from schools data
    const schoolOptions = schools.reduce((acc, school) => {
        acc[school.id] = `${school.school_name} (${school.district_name})`;
        return acc;
    }, {});

    const handleSchoolSearch = (schoolId) => {
        console.log('SearchSelect onChange triggered with value:', schoolId);
        if (schoolId) {
            const school = schools.find(s => s.id === schoolId);
            console.log('Found school:', school);
            if (school) {
                console.log('Setting selected school:', school);
                setSelectedSchool(school);
            }
        }
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value));
        setCurrentPage(0);
    };

    const toTitleCase = (str) => {
        if (!str) return '';
        return str.toLowerCase()
            .replace(/\b\w/g, l => l.toUpperCase())
            .replace(/\bIsd\b/g, 'ISD');
    };

    // Create unique options for filters
    const uniqueSchoolNames = [...new Set(schools.map(school => school.school_name))].sort();
    const uniqueDistrictNames = [...new Set(schools.map(school => school.district_name))].sort();
    
    const schoolNameOptions = uniqueSchoolNames.map(name => ({ id: name, name: toTitleCase(name) }));
    const districtNameOptions = uniqueDistrictNames.map(name => ({ id: name, name: toTitleCase(name) }));

    // Print functionality for main table
    const handlePrintTable = () => {
        const printWindow = window.open('', '_blank');
        const tableData = filteredSchools.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
        
        printWindow.document.write(`
            <html>
                <head>
                    <title>Schools Weather Impact Dashboard</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        h1 { color: #333; margin-bottom: 20px; }
                        table { border-collapse: collapse; width: 100%; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #f2f2f2; font-weight: bold; }
                        tr:nth-child(even) { background-color: #f9f9f9; }
                        .print-date { font-size: 12px; color: #666; margin-top: 20px; }
                        .filters-info { background-color: #f5f5f5; padding: 10px; margin-bottom: 20px; border-radius: 5px; }
                    </style>
                </head>
                <body>
                    <h1>Schools Weather Impact Dashboard</h1>
                    <div class="filters-info">
                        <strong>Applied Filters:</strong><br/>
                        ${searchTerm ? `Search Term: "${searchTerm}"<br/>` : ''}
                        ${selectedSchoolNames.length > 0 ? `School Names: ${selectedSchoolNames.join(', ')}<br/>` : ''}
                        ${selectedDistrictNames.length > 0 ? `District Names: ${selectedDistrictNames.join(', ')}<br/>` : ''}
                        Showing ${filteredSchools.length} total schools (Page ${currentPage + 1} of ${Math.ceil(filteredSchools.length / pageSize)})
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>School Name</th>
                                <th>District</th>
                                <th>City</th>
                                <th>County</th>
                                <th>Enrollment</th>
                                <th>Weather Events</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${tableData.map(school => `
                                <tr>
                                    <td>${toTitleCase(school.school_name)}</td>
                                    <td>${toTitleCase(school.district_name)}</td>
                                    <td>${toTitleCase(school.city)}</td>
                                    <td>${toTitleCase(school.county)}</td>
                                    <td>${school.enrollment > 0 ? school.enrollment : 'No data'}</td>
                                    <td>${school.event_count}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div class="print-date">Generated on: ${new Date().toLocaleDateString()}</div>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    // Export to XLSX functionality
    const handleExportToXLSX = () => {
        // Import XLSX dynamically to avoid bundle size issues
        import('xlsx').then((XLSX) => {
            const tableData = filteredSchools.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
            
            // Prepare data for Excel
            const excelData = tableData.map(school => ({
                'School Name': toTitleCase(school.school_name),
                'District': toTitleCase(school.district_name),
                'City': toTitleCase(school.city),
                'County': toTitleCase(school.county),
                'Enrollment': school.enrollment > 0 ? school.enrollment : 'No data',
                'Weather Events': school.event_count
            }));

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Add column widths
            const colWidths = [
                { wch: 30 }, // School Name
                { wch: 25 }, // District
                { wch: 20 }, // City
                { wch: 20 }, // County
                { wch: 12 }, // Enrollment
                { wch: 15 }  // Weather Events
            ];
            ws['!cols'] = colWidths;

            // Add the worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Schools Weather Data');

            // Generate filename with current date and filters
            const dateStr = new Date().toISOString().split('T')[0];
            let filename = `schools-weather-data-${dateStr}`;
            
            if (searchTerm) {
                filename += `-search-${searchTerm.replace(/[^a-zA-Z0-9]/g, '')}`;
            }
            if (selectedSchoolNames.length > 0) {
                filename += `-schools-${selectedSchoolNames.length}`;
            }
            if (selectedDistrictNames.length > 0) {
                filename += `-districts-${selectedDistrictNames.length}`;
            }
            
            filename += '.xlsx';

            // Save file
            XLSX.writeFile(wb, filename);
        }).catch(error => {
            console.error('Error loading XLSX library:', error);
            alert('Error exporting to Excel. Please try again.');
        });
    };

    // Handle adding school name to filter
    const handleSchoolNameAdd = (schoolName) => {
        if (schoolName && !selectedSchoolNames.includes(schoolName)) {
            setSelectedSchoolNames([...selectedSchoolNames, schoolName]);
        }
    };

    // Handle adding district name to filter
    const handleDistrictNameAdd = (districtName) => {
        if (districtName && !selectedDistrictNames.includes(districtName)) {
            setSelectedDistrictNames([...selectedDistrictNames, districtName]);
        }
    };

    // Handle removing school name from filter
    const handleRemoveSchoolName = (schoolName) => {
        setSelectedSchoolNames(selectedSchoolNames.filter(name => name !== schoolName));
    };

    // Handle removing district name from filter
    const handleRemoveDistrictName = (districtName) => {
        setSelectedDistrictNames(selectedDistrictNames.filter(name => name !== districtName));
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedSchoolNames([]);
        setSelectedDistrictNames([]);
    };

    // Check if any filters are active
    const hasActiveFilters = searchTerm !== '' || selectedSchoolNames.length > 0 || selectedDistrictNames.length > 0;

    return (
        <div className={`page-container ${theme}`}>
            <div id="page-header">
                <h2>Schools Weather Impact Dashboard</h2>
                <p className='subtext'>Click on any school to see its weather events from the last 2 years</p>
            </div>

            {/* Search Controls */}
            {showFilters && (
                <div className="search-controls">
                <div className="search-bar">
                    <SearchSelect
                        onChange={handleSchoolSearch}
                        options={schoolOptions}
                        placeholder="Search schools by name, city, district..."
                        value=""
                    />
                </div>
                
                <div className="filters-controls">
                    <div className="filter-group">
                        <label className='subtext'>Add School Name Filter:</label>
                        <SearchSelect
                            options={schoolNameOptions.reduce((acc, option) => {
                                acc[option.id] = option.name;
                                return acc;
                            }, {})}
                            value=""
                            onChange={handleSchoolNameAdd}
                            placeholder="Search and select school name..."
                        />
                        {selectedSchoolNames.length > 0 && (
                            <div className="filter-tags">
                                {selectedSchoolNames.map(name => (
                                    <span key={name} className="filter-tag">
                                        {toTitleCase(name)}
                                        <button 
                                            onClick={() => handleRemoveSchoolName(name)}
                                            className="remove-tag"
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="filter-group">
                        <label className='subtext'>Add District Name Filter:</label>
                        <SearchSelect
                            options={districtNameOptions.reduce((acc, option) => {
                                acc[option.id] = option.name;
                                return acc;
                            }, {})}
                            value=""
                            onChange={handleDistrictNameAdd}
                            placeholder="Search and select district name..."
                        />
                        {selectedDistrictNames.length > 0 && (
                            <div className="filter-tags">
                                {selectedDistrictNames.map(name => (
                                    <span key={name} className="filter-tag">
                                        {toTitleCase(name)}
                                        <button 
                                            onClick={() => handleRemoveDistrictName(name)}
                                            className="remove-tag"
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    <div className="page-size-control">
                        <div className="filter-group">
                            <label className='subtext'>Show:</label>
                            <div className="select-wrapper">
                                <select className='filter-select' value={pageSize} onChange={handlePageSizeChange}>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={500}>500</option>
                                    <option value={1000}>1000</option>
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                        <label className='subtext' style={{ marginTop: '14.5px' }}>
                            per page
                        </label>
                    </div>
                    
                    <div className="action-controls">
                        <button 
                            className="action quaternary"
                            onClick={handlePrintTable}
                            title="Print Current Page"
                        >
                            <Printer size={16} />
                            Print
                        </button>
                        
                        <button 
                            className="action secondary"
                            onClick={handleExportToXLSX}
                            title="Export to Excel"
                        >
                            <Download size={16} />
                            Excel
                        </button>
                        
                        {hasActiveFilters && (
                            <button 
                                className="action tertiary"
                                onClick={handleClearFilters}
                                title="Clear All Filters"
                            >
                                <X size={16} />
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>
            </div>
            )}

            {/* Filter Toggle Header */}
            <div className="filter-toggle-header">
                <button 
                    className="toggle-filters-button"
                    onClick={() => setShowFilters(!showFilters)}
                    title={showFilters ? "Hide Filters" : "Show Filters"}
                >
                    <Filter size={16} />
                    {showFilters ? "Hide Filters" : "Show Filters"}
                </button>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="loading-container">
                    <Loader2 className='spinner' color="var(--fill)" />
                </div>
            )}

            {/* Results Table */}
            {!loading && (
                <>
                    <div className="results-info">
                        <p>Found {filteredSchools.length} schools</p>
                        {hasActiveFilters && (
                            <div className="active-filters-summary">
                                <strong>Active Filters:</strong>
                                {searchTerm && <span className="filter-tag">Search: "{searchTerm}"</span>}
                                {selectedSchoolNames.length > 0 && (
                                    <span className="filter-tag">
                                        Schools: {selectedSchoolNames.length} selected
                                    </span>
                                )}
                                {selectedDistrictNames.length > 0 && (
                                    <span className="filter-tag">
                                        Districts: {selectedDistrictNames.length} selected
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <table className="weather-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('school_name')} className="sortable">
                                    School Name {sortColumn === 'school_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('district_name')} className="sortable">
                                    District {sortColumn === 'district_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('city')} className="sortable hide-mobile">
                                    City {sortColumn === 'city' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('county')} className="sortable hide-mobile">
                                    County {sortColumn === 'county' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('enrollment')} className="sortable hide-mobile">
                                    Enrollment {sortColumn === 'enrollment' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('event_count')} className="sortable">
                                    Weather Events {sortColumn === 'event_count' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentPageData.map((school, index) => (
                                <tr 
                                    key={school.id} 
                                    onClick={() => handleRowClick(school)}
                                    className='small-text'
                                >
                                    <td>{toTitleCase(school.school_name)}</td>
                                    <td>{toTitleCase(school.district_name)}</td>
                                    <td className="hide-mobile">{toTitleCase(school.city)}</td>
                                    <td className="hide-mobile">{toTitleCase(school.county)}</td>
                                    <td className="hide-mobile">{school.enrollment > 0 ? school.enrollment : 'No data'}</td>
                                    <td className={school.event_count > 0 ? 'event-count-positive' : 'event-count-zero'}>
                                        {school.event_count}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Pages
                            data={filteredSchools}
                            pageSize={pageSize}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            handlePageChange={handlePageChange}
                            set={false}
                        />
                    )}
                </>
            )}

            {/* School Events Modal */}
            {selectedSchool && (
                <SchoolEventsTable
                    schoolId={selectedSchool.id}
                    schoolName={selectedSchool.school_name}
                    onClose={() => setSelectedSchool(null)}
                />
            )}
        </div>
    );
}
